import React, { useMemo } from 'react';
import i18next from 'i18next';
import {
  OtoButtons,
  TableCellProps,
  SimpleTableWithSorting,
  TableDateFormatHelper,
  generateColumn,
  getRTOriginal,
} from 'app/components/common';
import type { IDelivery, IFormattedDeliveryWithAdminData } from 'types';
import { toDateTimeInputValue } from 'utils/date-time';
import OrderDelivery from 'app/components/orders/order-parts/OrderDelivery';
import OrderPaymentType from 'app/components/orders/order-parts/OrderPaymentType';
import OrderTotal from 'app/components/orders/order-parts/OrderTotal';

type DeliveryType = IDelivery | IFormattedDeliveryWithAdminData;

export type DeliveriesTableProps = {
  deliveries: DeliveryType[];
  isLoading?: (delivery: DeliveryType) => boolean;
  onUnassignDriver?: (delivery: DeliveryType) => void;
  showOrderDetails?: boolean;
  showPrices?: boolean;
  showTimes?: boolean;
  showPhoneNumber?: boolean;
};

const DeliveriesTable: React.FC<DeliveriesTableProps> = ({
  deliveries,
  isLoading,
  onUnassignDriver,
  showOrderDetails,
  showPrices,
  showTimes,
  showPhoneNumber,
}) => {
  const columns = useMemo(
    () => [
      generateColumn('created_at', {
        customHeader: i18next.t('deliveries-table.created-at'),
        customCellFormatter: (props: TableCellProps<DeliveryType>) => {
          const delivery = getRTOriginal<DeliveryType>(props);
          return toDateTimeInputValue(new Date(delivery.created_at));
        },
      }),
      generateColumn('address', {
        customHeader: i18next.t('deliveries-table.address'),
      }),
      ...(showPhoneNumber
        ? [
            generateColumn('phone', {
              customHeader: 'Numer telefonu',
            }),
          ]
        : []),
      generateColumn('distance', {
        customHeader: i18next.t('deliveries-table.distance'),
      }),
      generateColumn('price', {
        customHeader: i18next.t('deliveries-table.price'),
      }),
      generateColumn('payment_status', {
        customHeader: i18next.t('deliveries-table.payment-type'),
      }),
      generateColumn('restaurant_id', {
        customHeader: i18next.t('deliveries-table.restaurant'),
        customCellFormatter: (props: TableCellProps<DeliveryType>) => {
          const delivery = getRTOriginal<DeliveryType>(props);
          return delivery.restaurant
            ? `${delivery.restaurant.name} (${delivery.restaurant.id})`
            : delivery.restaurant_id;
        },
      }),
      ...(showOrderDetails
        ? [
            generateColumn('order_id', {
              customHeader: i18next.t('deliveries-table.order'),
              customCellFormatter: (
                props: TableCellProps<DeliveryType>
              ) => {
                const delivery = getRTOriginal<DeliveryType>(props);
                if (!('order' in delivery) || !delivery.order) {
                  return delivery.order_id;
                }

                return (
                  <>
                    <OrderDelivery order={delivery.order} />
                    <hr className={'my-1'} />
                    <OrderPaymentType order={delivery.order} />
                    <hr className={'my-1'} />
                    <OrderTotal order={delivery.order} showPrefix={false} />
                  </>
                );
              },
            }),
          ]
        : []),
      generateColumn('assigned_to', {
        customHeader: i18next.t('deliveries-table.driver'),
        customCellFormatter: (props: TableCellProps<DeliveryType>) => {
          const delivery = getRTOriginal<DeliveryType>(props);
          return delivery.user
            ? `${delivery.user.name} (${delivery.user.id})`
            : delivery.assigned_to;
        },
      }),
      generateColumn('driver_type', {
        customHeader: 'Driver Type',
        filterType: 'select',
      }),
      generateColumn('status', {
        customHeader: 'Status',
        filterType: 'select',
      }),
      ...(showPrices
        ? [
            generateColumn('priceForDriver', {
              customHeader: i18next.t('deliveries-table.driver-pay-gross'),
            }),
            generateColumn('nettoCostForRestaurant', {
              customHeader: i18next.t('deliveries-table.restaurant-cost-net'),
            }),
          ]
        : []),
      ...(showTimes
        ? [
            generateColumn('collectionTime', {
              customHeader: i18next.t('deliveries-table.collection-time'),
              customCellFormatter:
                TableDateFormatHelper<IFormattedDeliveryWithAdminData>(
                  'collectionTime',
                  'HH:mm dd.MM.yy'
                ),
            }),
            generateColumn('deliveredTime', {
              customHeader: i18next.t('deliveries-table.delivered-time'),
              customCellFormatter:
                TableDateFormatHelper<IFormattedDeliveryWithAdminData>(
                  'deliveredTime',
                  'HH:mm dd.MM.yy'
                ),
            }),
          ]
        : []),
      generateColumn('id', {
        customHeader: 'ID',
        customCellFormatter: (props: TableCellProps<DeliveryType>) => {
          const delivery = getRTOriginal<DeliveryType>(props);
          return (
            <>
              <div>{delivery.id}</div>
              {onUnassignDriver && delivery.assigned_to && (
                <OtoButtons.ReloadButton
                  outline
                  color={'secondary'}
                  loading={isLoading?.(delivery)}
                  onClick={() => onUnassignDriver(delivery)}
                >
                  {i18next.t('deliveries-table.unassign-driver')}
                </OtoButtons.ReloadButton>
              )}
            </>
          );
        },
      }),
    ],
    [isLoading, onUnassignDriver, showOrderDetails, showPrices, showTimes, showPhoneNumber]
  );
  return (
    <>
      <SimpleTableWithSorting columns={columns} data={deliveries} hasFilters />
    </>
  );
};

export default DeliveriesTable;
